import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { endpoints, formatApiError } from "../lib/api";
import { getAll, removeOne, STORES } from "../lib/db";
import { formatCurrency } from "../lib/romEngine";
import { RomImportModal, InvoiceImportModal } from "../components/ImportModals";
import { VarianceAnalysis } from "../components/VarianceAnalysis";
import { BillingStatusBadge } from "../components/BillingStatusBadge";
import QuickLogTimeModal from "../components/QuickLogTimeModal";
import {
    Calculator as CalcIcon, Plus, MagnifyingGlass, CaretUp, CaretDown,
    ShareNetwork, FileText, CheckCircle, PencilSimple, Trash, CurrencyDollar, X,
    FileXls, FilePdf, CheckSquare, Square, House, Buildings, Clock, Kanban, ChartBar,
    DotsThreeVertical, CaretRight,
} from "@phosphor-icons/react";

const FILTERS = [
    { key: "all", label: "All" },
    { key: "draft", label: "Draft" },
    { key: "complete", label: "Complete" },
    { key: "shared", label: "Shared" },
];

const SCALE_TABS = [
    { key: "all", label: "All", icon: null },
    { key: "residential", label: "Residential", icon: House },
    { key: "commercial", label: "Commercial", icon: Buildings },
];

const COLS = [
    { key: "ref", label: "Ref", sortable: true },
    { key: "loss_type", label: "Loss", sortable: true },
    { key: "client_name", label: "Client", sortable: true },
    { key: "address", label: "Site", sortable: true },
    { key: "status_combined", label: "Status", sortable: true },
    { key: "rom", label: "ROM total", sortable: true, numeric: true },
    { key: "actual", label: "Actual", sortable: true, numeric: true },
    { key: "variance", label: "Variance", sortable: true, numeric: true },
    { key: "updated_at", label: "Last updated", sortable: true },
];

const BD_KEYS = [
    { k: "labour", label: "A · Field Labour" },
    { k: "supervision", label: "A · PM / Supervision" },
    { k: "consumables", label: "B · Consumables" },
    { k: "equipment", label: "C · Equipment" },
    { k: "subcontractors", label: "D · Subcontractors" },
    { k: "misc", label: "E · Misc / Incidentals" },
];

export default function Estimates() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [jobs, setJobs] = useState([]);
    const [clients, setClients] = useState([]);
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [filter, setFilter] = useState("all");
    const [scaleTab, setScaleTab] = useState("all");
    const [sort, setSort] = useState({ key: "updated_at", dir: "desc" });
    const [confirmDel, setConfirmDel] = useState(null);
    const [showRomImport, setShowRomImport] = useState(false);
    const [showInvoiceImport, setShowInvoiceImport] = useState(false);
    const [selected, setSelected] = useState(new Set());
    const [bulkConfirm, setBulkConfirm] = useState(false);
    const [bulkBusy, setBulkBusy] = useState(false);
    const [notice, setNotice] = useState(null);
    const [mobileMoreOpen, setMobileMoreOpen] = useState(false);

    // Record-actual-cost modal state
    const [recording, setRecording] = useState(null);
    const [actualValue, setActualValue] = useState("");
    const [tier, setTier] = useState("A");
    const [breakdown, setBreakdown] = useState({ labour: "", supervision: "", consumables: "", equipment: "", subcontractors: "", misc: "" });
    const [subActuals, setSubActuals] = useState({});
    const [err, setErr] = useState("");

    const [varianceJobId, setVarianceJobId] = useState(null);
    const [logJob, setLogJob] = useState(null);
    const [converting, setConverting] = useState(null);

    const canDelete = user?.role === "manager" || user?.role === "admin";

    useEffect(() => { load(); }, []);
    useEffect(() => {
        if (!notice) return;
        const t = setTimeout(() => setNotice(null), 4000);
        return () => clearTimeout(t);
    }, [notice]);

    async function load() {
        setLoading(true);
        try {
            const [j, c, d] = await Promise.all([endpoints.jobs(), endpoints.clients(), endpoints.docs()]);
            setJobs(j.data || []);
            setClients(c.data || []);
            setDocs(d.data || []);
        } catch {
            const [j, c, d] = await Promise.all([
                getAll(STORES.jobs), getAll(STORES.clients), getAll(STORES.documents),
            ]);
            setJobs(j); setClients(c); setDocs(d);
        }
        setLoading(false);
    }

    async function convertToProject(job) {
        setConverting(job.id);
        try {
            const { data } = await endpoints.projectFromRom(job.id);
            navigate(`/projects/${data.id}`);
        } catch (e) {
            setNotice({ type: "err", msg: formatApiError(e) });
        } finally {
            setConverting(null);
        }
    }

    const cmap = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);
    const sharedJobIds = useMemo(() => {
        const ids = new Set();
        for (const d of docs) {
            if (d.job_id && ["sent", "viewed", "signed"].includes(d.status)) ids.add(d.job_id);
        }
        return ids;
    }, [docs]);

    const rows = useMemo(() => {
        const enriched = jobs.map((j) => {
            const isShared = sharedJobIds.has(j.id);
            const isComplete = ["closed", "approved"].includes(j.status) || !!j.actual_cost;
            const isDraft = j.status === "draft" && !isShared && !isComplete;
            const statuses = [];
            if (isComplete) statuses.push("complete");
            if (isShared) statuses.push("shared");
            if (isDraft) statuses.push("draft");
            if (!statuses.length) statuses.push(j.status || "draft");
            const approval = j.approval || null;
            const finalStatuses = approval ? statuses.filter((s) => s !== "complete" && s !== "draft") : statuses;
            const rom = j.result?.grand_total || 0;
            const actual = j.actual_cost || 0;
            const variance = (actual && rom) ? ((actual - rom) / rom * 100) : null;
            return {
                ...j,
                client_name: cmap[j.client_id]?.name || "",
                rom, actual, variance,
                scale: j.property_scale || "commercial",
                statuses: finalStatuses,
                status_combined: (approval ? `approved_${approval.recipient_type}` : finalStatuses[0]) || "",
                approval,
                shared: isShared,
                complete: isComplete,
                draft: isDraft,
            };
        });

        const filtered = enriched.filter((r) => {
            if (filter === "draft" && !r.draft) return false;
            if (filter === "complete" && !r.complete) return false;
            if (filter === "shared" && !r.shared) return false;
            if (scaleTab !== "all" && r.scale !== scaleTab) return false;
            if (!q) return true;
            const hay = `${r.ref} ${r.address} ${r.loss_type} ${r.claim_number} ${r.client_name}`.toLowerCase();
            return hay.includes(q.toLowerCase());
        });

        const sorted = [...filtered].sort((a, b) => {
            const av = a[sort.key] ?? "";
            const bv = b[sort.key] ?? "";
            const dir = sort.dir === "asc" ? 1 : -1;
            if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
            return String(av).localeCompare(String(bv)) * dir;
        });
        return sorted;
    }, [jobs, cmap, sharedJobIds, q, filter, scaleTab, sort]);

    const scaleCounts = useMemo(() => {
        const c = { all: jobs.length, residential: 0, commercial: 0 };
        for (const j of jobs) {
            const s = j.property_scale || "commercial";
            if (s === "residential") c.residential++; else c.commercial++;
        }
        return c;
    }, [jobs]);

    const counts = useMemo(() => {
        const c = { all: 0, draft: 0, complete: 0, shared: 0 };
        for (const j of jobs) {
            if (scaleTab !== "all" && (j.property_scale || "commercial") !== scaleTab) continue;
            c.all++;
            const isShared = sharedJobIds.has(j.id);
            const isComplete = ["closed", "approved"].includes(j.status) || !!j.actual_cost;
            const isDraft = j.status === "draft" && !isShared && !isComplete;
            if (isShared) c.shared++;
            if (isComplete) c.complete++;
            if (isDraft) c.draft++;
        }
        return c;
    }, [jobs, sharedJobIds, scaleTab]);

    function setSortKey(k) {
        if (sort.key === k) setSort({ key: k, dir: sort.dir === "asc" ? "desc" : "asc" });
        else setSort({ key: k, dir: "desc" });
    }

    async function deleteJob(j) {
        setConfirmDel(null);
        const prev = jobs;
        setJobs((list) => list.filter((x) => x.id !== j.id));
        try {
            await endpoints.deleteJob(j.id);
            await removeOne(STORES.jobs, j.id).catch(() => {});
            setNotice({ type: "ok", msg: `Deleted ${j.ref}` });
        } catch (e) {
            setJobs(prev);
            setNotice({ type: "err", msg: `Could not delete ${j.ref}: ${formatApiError(e)}` });
        }
    }

    function toggleSel(id) {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }
    const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
    function toggleAll() {
        setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.id)));
    }
    async function bulkDelete() {
        setBulkBusy(true);
        const ids = [...selected];
        const prev = jobs;
        setJobs((list) => list.filter((x) => !selected.has(x.id)));
        try {
            const { data } = await endpoints.bulkDeleteJobs(ids);
            await Promise.all(ids.map((id) => removeOne(STORES.jobs, id).catch(() => {})));
            setNotice({ type: "ok", msg: `Deleted ${data?.deleted ?? ids.length} estimate${ids.length > 1 ? "s" : ""}` });
        } catch (e) {
            setJobs(prev);
            setNotice({ type: "err", msg: `Could not delete: ${formatApiError(e)}` });
        }
        setSelected(new Set());
        setBulkConfirm(false);
        setBulkBusy(false);
    }

    // ── Record-actual-cost helpers ──────────────────────────────────────
    const breakdownSum = BD_KEYS.reduce((s, { k }) => s + (Number(breakdown[k]) || 0), 0);
    const effectiveTotal = tier === "B" ? breakdownSum : Number(actualValue);

    function openRecord(j) {
        setRecording(j);
        const a = j.actual || {};
        setTier(a.tier || "A");
        setActualValue(a.total || j.actual_cost || "");
        const bd = a.breakdown || {};
        setBreakdown({
            labour: bd.labour ?? "", supervision: bd.supervision ?? "", consumables: bd.consumables ?? "", equipment: bd.equipment ?? "",
            subcontractors: bd.subcontractors ?? "", misc: bd.misc ?? "",
        });
        const prior = {};
        (a.sub_actuals || []).forEach((s) => { prior[s.item] = s.actual; });
        const init = {};
        (j.result?.subcontractor_lines || []).forEach((l, i) => {
            if (prior[l.item] != null) init[i] = prior[l.item];
        });
        setSubActuals(init);
    }

    function estimateSubLines() {
        const lines = recording?.result?.subcontractor_lines || [];
        const inputs = recording?.inputs?.subcontractors || [];
        return lines.map((l, i) => ({
            idx: i,
            item: l.item,
            vendor: inputs[i]?.vendor || "",
            category: l.category || inputs[i]?.category || "trade",
            quoted: l.subtotal,
        }));
    }

    async function saveActual() {
        if (!recording || !effectiveTotal || effectiveTotal <= 0) return;
        const sub_actuals = tier === "B"
            ? estimateSubLines()
                .filter((s) => subActuals[s.idx] !== undefined && subActuals[s.idx] !== "")
                .map((s) => ({ item: s.item, vendor: s.vendor, category: s.category, quoted: s.quoted, actual: Number(subActuals[s.idx]) || 0 }))
            : [];
        const payload = tier === "B"
            ? {
                actual_cost: breakdownSum,
                tier: "B",
                breakdown: Object.fromEntries(BD_KEYS.map(({ k }) => [k, Number(breakdown[k]) || 0])),
                sub_actuals,
              }
            : { actual_cost: Number(actualValue), tier: "A" };
        try {
            await endpoints.recordActual(recording.id, payload);
            setRecording(null);
            setActualValue("");
            setTier("A");
            setBreakdown({ labour: "", supervision: "", consumables: "", equipment: "", subcontractors: "", misc: "" });
            setSubActuals({});
            setErr("");
            await load();
        } catch (e) {
            setErr(formatApiError(e));
        }
    }

    return (
        <div className="max-w-[1600px] mx-auto px-6 py-8">
            <header className="flex flex-wrap items-end justify-between gap-4 mb-6">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--rom-text-3)] font-semibold mb-1">
                        Estimates
                    </div>
                    <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight flex items-center gap-3">
                        <CalcIcon size={28} weight="bold" className="text-[var(--rom-orange)]" />
                        Estimates
                    </h1>
                    <p className="text-sm text-[var(--rom-text-2)] mt-1.5 max-w-2xl">
                        Every ROM you and your team have started, residential and commercial. Open one to edit, track its outcome, or convert it into a project.
                    </p>
                </div>
                {/* Desktop / tablet — unchanged */}
                <div className="hidden sm:flex items-center gap-2">
                    <button onClick={() => setShowRomImport(true)} className="btn btn-ghost" data-testid="import-rom-btn">
                        <FileXls size={14} weight="bold" className="text-[var(--rom-green)]" /> Import ROM
                    </button>
                    <button onClick={() => setShowInvoiceImport(true)} className="btn btn-ghost" data-testid="import-invoice-btn">
                        <FilePdf size={14} weight="bold" className="text-[var(--rom-orange)]" /> Import Invoice
                    </button>
                    <button onClick={() => navigate("/calculator/new?scale=residential")} className="btn btn-ghost" data-testid="create-new-estimate">
                        <Plus size={14} weight="bold" className="text-[var(--rom-green)]" /> New Estimate <span className="text-[var(--rom-text-3)] font-normal ml-1">(residential)</span>
                    </button>
                    <button onClick={() => navigate("/calculator/new?scale=commercial")} className="btn btn-primary" data-testid="create-new-rom">
                        <Plus size={14} weight="bold" /> New ROM <span className="opacity-70 font-normal ml-1">(commercial)</span>
                    </button>
                </div>

                {/* Phone — New ROM first & full-width, Import actions tucked into a small More menu */}
                <div className="flex flex-col gap-2 w-full sm:hidden">
                    <button onClick={() => navigate("/calculator/new?scale=commercial")} className="btn btn-primary w-full justify-center" data-testid="create-new-rom-mobile">
                        <Plus size={16} weight="bold" /> New ROM <span className="opacity-70 font-normal ml-1">(commercial)</span>
                    </button>
                    <div className="flex items-center gap-2">
                        <button onClick={() => navigate("/calculator/new?scale=residential")} className="btn btn-ghost flex-1 justify-center" data-testid="create-new-estimate-mobile">
                            <Plus size={16} weight="bold" className="text-[var(--rom-green)]" /> New Estimate
                        </button>
                        <div className="relative shrink-0">
                            <button onClick={() => setMobileMoreOpen((o) => !o)} className="btn btn-ghost px-3" title="More" data-testid="estimates-mobile-more-btn">
                                <DotsThreeVertical size={18} weight="bold" />
                            </button>
                            {mobileMoreOpen && (
                                <>
                                    <div className="fixed inset-0 z-40" onClick={() => setMobileMoreOpen(false)} />
                                    <div className="absolute right-0 mt-1 w-52 bg-white rounded-[6px] shadow-xl border border-[var(--rom-border)] z-50 overflow-hidden py-1" data-testid="estimates-mobile-more-menu">
                                        <button onClick={() => { setMobileMoreOpen(false); setShowRomImport(true); }} className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-left hover:bg-slate-50" data-testid="import-rom-btn-mobile">
                                            <FileXls size={15} weight="bold" className="text-[var(--rom-green)]" /> Import ROM
                                        </button>
                                        <button onClick={() => { setMobileMoreOpen(false); setShowInvoiceImport(true); }} className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-left hover:bg-slate-50" data-testid="import-invoice-btn-mobile">
                                            <FilePdf size={15} weight="bold" className="text-[var(--rom-orange)]" /> Import Invoice
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            <div className="flex items-center gap-1 border-b border-[var(--rom-border)] mb-4 overflow-x-auto" data-testid="estimates-scale-tabs">
                {SCALE_TABS.map((t) => {
                    const Icon = t.icon;
                    const activeTab = scaleTab === t.key;
                    return (
                        <button
                            key={t.key}
                            onClick={() => setScaleTab(t.key)}
                            data-testid={`scale-tab-${t.key}`}
                            className={`shrink-0 whitespace-nowrap px-4 py-2.5 -mb-px text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 ${
                                activeTab ? "border-[var(--rom-orange)] text-[var(--rom-text)]" : "border-transparent text-[var(--rom-text-2)] hover:text-[var(--rom-text)]"
                            }`}
                        >
                            {Icon && <Icon size={14} weight="bold" />}
                            {t.label}
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${activeTab ? "bg-[var(--rom-orange-soft)] text-[var(--rom-orange-dark)]" : "bg-slate-100 text-[var(--rom-text-3)]"}`}>
                                {scaleCounts[t.key]}
                            </span>
                        </button>
                    );
                })}
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
                <div className="flex items-center gap-1 bg-white border border-[var(--rom-border)] rounded-[6px] p-1 overflow-x-auto max-w-full" data-testid="rom-filters">
                    {FILTERS.map((f) => (
                        <button
                            key={f.key}
                            onClick={() => setFilter(f.key)}
                            data-testid={`filter-${f.key}`}
                            className={`shrink-0 whitespace-nowrap px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-[4px] transition-colors flex items-center gap-2 ${
                                filter === f.key ? "bg-[var(--rom-navy)] text-white" : "text-[var(--rom-text-2)] hover:text-[var(--rom-text)]"
                            }`}
                        >
                            {f.label}
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${filter === f.key ? "bg-white/15" : "bg-slate-100"}`}>
                                {counts[f.key]}
                            </span>
                        </button>
                    ))}
                </div>

                <div className="relative w-full sm:flex-1 sm:max-w-md">
                    <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--rom-text-3)]" />
                    <input
                        className="input pl-9"
                        placeholder="Search ref, client, address, claim…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        data-testid="rom-search"
                    />
                </div>
            </div>

            {canDelete && selected.size > 0 && (
                <div className="flex items-center justify-between bg-[var(--rom-navy)] text-white rounded-[6px] px-4 py-2.5 mb-3" data-testid="bulk-toolbar">
                    <span className="text-sm font-semibold">{selected.size} selected</span>
                    <div className="flex items-center gap-2">
                        <button className="text-xs text-white/70 hover:text-white" onClick={() => setSelected(new Set())} data-testid="bulk-clear">Clear</button>
                        <button className="btn btn-danger" onClick={() => setBulkConfirm(true)} data-testid="bulk-delete-btn">
                            <Trash size={14} weight="bold" /> Delete selected
                        </button>
                    </div>
                </div>
            )}

            <div className="rom-card overflow-hidden hidden sm:block">
                <table className="rom-table" data-testid="rom-list-table">
                    <thead>
                        <tr>
                            {canDelete && (
                                <th className="w-8">
                                    <button onClick={toggleAll} className="flex items-center" title="Select all" data-testid="select-all">
                                        {allSelected ? <CheckSquare size={16} weight="fill" className="text-[var(--rom-orange)]" /> : <Square size={16} />}
                                    </button>
                                </th>
                            )}
                            {COLS.map((c) => (
                                <th key={c.key} className={c.numeric ? "numeric" : ""}>
                                    <button
                                        onClick={() => setSortKey(c.key)}
                                        className="flex items-center gap-1 hover:text-[var(--rom-text)]"
                                        data-testid={`sort-${c.key}`}
                                    >
                                        {c.label}
                                        {sort.key === c.key && (sort.dir === "asc" ? <CaretUp size={10} weight="bold" /> : <CaretDown size={10} weight="bold" />)}
                                    </button>
                                </th>
                            ))}
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr><td colSpan={COLS.length + 2} className="text-center text-[var(--rom-text-3)] py-10">Loading…</td></tr>
                        )}
                        {!loading && rows.length === 0 && (
                            <tr><td colSpan={COLS.length + 2} className="text-center py-14" data-testid="rom-empty">
                                <div className="text-[var(--rom-text-3)] mb-3">
                                    {q || filter !== "all" || scaleTab !== "all"
                                        ? "No estimates match these filters."
                                        : "No estimates yet — create your first ROM to get started."}
                                </div>
                                {!q && filter === "all" && (
                                    <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
                                        <button onClick={() => navigate("/calculator/new?scale=residential")} className="btn btn-ghost">
                                            <Plus size={14} weight="bold" className="text-[var(--rom-green)]" /> New Estimate
                                        </button>
                                        <button onClick={() => navigate("/calculator/new?scale=commercial")} className="btn btn-primary">
                                            <Plus size={14} weight="bold" /> New ROM
                                        </button>
                                    </div>
                                )}
                            </td></tr>
                        )}
                        {!loading && rows.map((r) => (
                            <tr key={r.id} className="cursor-pointer" onClick={() => navigate(`/calculator/${r.id}`)} data-testid={`rom-row-${r.ref}`}>
                                {canDelete && (
                                    <td onClick={(e) => e.stopPropagation()} className="w-8">
                                        <button onClick={() => toggleSel(r.id)} className="flex items-center" data-testid={`select-${r.ref}`}>
                                            {selected.has(r.id) ? <CheckSquare size={16} weight="fill" className="text-[var(--rom-orange)]" /> : <Square size={16} className="text-[var(--rom-text-3)]" />}
                                        </button>
                                    </td>
                                )}
                                <td className="font-mono text-xs font-semibold">{r.ref}</td>
                                <td className="capitalize">{r.loss_type}</td>
                                <td>{r.client_name || <span className="text-[var(--rom-text-3)]">—</span>}</td>
                                <td className="text-xs text-[var(--rom-text-2)] max-w-xs truncate">{r.address || "—"}</td>
                                <td><StatusChips statuses={r.statuses} approval={r.approval} billingStatus={r.billing_status} /></td>
                                <td className="numeric font-semibold">{r.rom ? formatCurrency(r.rom) : "—"}</td>
                                <td className="numeric">{r.actual ? formatCurrency(r.actual) : "—"}</td>
                                <td className="numeric">{r.variance !== null ? <span className={r.variance > 0 ? "text-amber-700" : "text-[var(--rom-green)]"}>{r.variance > 0 ? "+" : ""}{r.variance.toFixed(1)}%</span> : "—"}</td>
                                <td className="text-xs text-[var(--rom-text-2)]">{fmtDate(r.updated_at)}</td>
                                <td onClick={(e) => e.stopPropagation()} className="text-right">
                                    <div className="flex items-center gap-1 justify-end">
                                        <button onClick={() => navigate(`/calculator/${r.id}`)} className="p-1.5 rounded text-[var(--rom-text-2)] hover:bg-slate-100 hover:text-[var(--rom-text)]" title="Open" data-testid={`rom-open-${r.ref}`}>
                                            <PencilSimple size={14} weight="bold" />
                                        </button>
                                        <button onClick={() => setLogJob({ id: r.id, ref: r.ref })} className="p-1.5 rounded text-[var(--rom-text-2)] hover:bg-slate-100 hover:text-[var(--rom-text)]" title="Log labour time to this estimate" data-testid={`log-time-${r.ref}`}>
                                            <Clock size={14} weight="bold" />
                                        </button>
                                        <button onClick={() => convertToProject(r)} disabled={converting === r.id} className="p-1.5 rounded text-[var(--rom-text-2)] hover:bg-slate-100 hover:text-[var(--rom-orange-dark)] disabled:opacity-50" title="Convert this ROM into a project" data-testid={`convert-project-${r.ref}`}>
                                            <Kanban size={14} weight="bold" />
                                        </button>
                                        {r.actual > 0 && (
                                            <button onClick={() => setVarianceJobId(r.id)} className="p-1.5 rounded text-[var(--rom-text-2)] hover:bg-slate-100 hover:text-[var(--rom-orange-dark)]" title="Variance analysis (ROM vs actual)" data-testid={`variance-btn-${r.ref}`}>
                                                <ChartBar size={14} weight="bold" />
                                            </button>
                                        )}
                                        <button onClick={() => openRecord(r)} className="p-1.5 rounded text-[var(--rom-text-2)] hover:bg-slate-100 hover:text-[var(--rom-text)]" title="Record final T&M cost" data-testid={`record-actual-${r.ref}`}>
                                            <CurrencyDollar size={14} weight="bold" />
                                        </button>
                                        {canDelete && (
                                            <button onClick={() => setConfirmDel(r)} className="p-1.5 rounded text-[var(--rom-text-2)] hover:bg-red-50 hover:text-[var(--rom-danger)]" title="Delete" data-testid={`rom-delete-${r.ref}`}>
                                                <Trash size={14} weight="bold" />
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Phone — card list replaces the table */}
            <div className="sm:hidden space-y-2.5" data-testid="rom-list-cards">
                {loading && (
                    <div className="text-center text-[var(--rom-text-3)] py-10">Loading…</div>
                )}
                {!loading && rows.length === 0 && (
                    <div className="text-center py-14" data-testid="rom-empty-mobile">
                        <div className="text-[var(--rom-text-3)] mb-3 px-4">
                            {q || filter !== "all" || scaleTab !== "all"
                                ? "No estimates match these filters."
                                : "No estimates yet — create your first ROM to get started."}
                        </div>
                    </div>
                )}
                {!loading && rows.map((r) => (
                    <button
                        key={r.id}
                        onClick={() => navigate(`/calculator/${r.id}`)}
                        className="rom-card w-full text-left p-4 active:bg-slate-50"
                        data-testid={`rom-card-${r.ref}`}
                    >
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                    <span className="font-mono text-xs font-semibold text-[var(--rom-text-2)]">{r.ref}</span>
                                    <span className="text-xs capitalize text-[var(--rom-text-3)]">· {r.loss_type}</span>
                                </div>
                                <div className="font-semibold truncate mt-0.5">{r.client_name || <span className="text-[var(--rom-text-3)] font-normal">No client name</span>}</div>
                            </div>
                            <CaretRight size={16} className="text-[var(--rom-text-3)] shrink-0 mt-0.5" />
                        </div>
                        <div className="flex items-center justify-between gap-3 mt-3">
                            <div className="font-display font-black text-lg text-[var(--rom-navy)]">{r.rom ? formatCurrency(r.rom) : "—"}</div>
                            <div className="text-[11px] text-[var(--rom-text-3)]">{fmtDate(r.updated_at)}</div>
                        </div>
                        <div className="mt-2">
                            <StatusChips statuses={r.statuses} approval={r.approval} billingStatus={r.billing_status} />
                        </div>
                    </button>
                ))}
            </div>

            {confirmDel && (
                <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
                    <div className="rom-card w-full max-w-md">
                        <div className="rom-card-head"><h3>Delete estimate?</h3></div>
                        <div className="rom-card-body space-y-3">
                            <p className="text-sm">
                                Are you sure you want to permanently delete <b className="font-mono">{confirmDel.ref}</b>?
                                This cannot be undone.
                            </p>
                            <div className="flex justify-end gap-2">
                                <button className="btn btn-ghost" onClick={() => setConfirmDel(null)}>Cancel</button>
                                <button className="btn btn-danger" onClick={() => deleteJob(confirmDel)} data-testid="confirm-delete">
                                    <Trash size={14} weight="bold" /> Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {bulkConfirm && (
                <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
                    <div className="rom-card w-full max-w-md">
                        <div className="rom-card-head"><h3>Delete {selected.size} estimate{selected.size > 1 ? "s" : ""}?</h3></div>
                        <div className="rom-card-body space-y-3">
                            <p className="text-sm">
                                This permanently deletes <b>{selected.size}</b> selected estimate{selected.size > 1 ? "s" : ""}. This cannot be undone.
                            </p>
                            <div className="flex justify-end gap-2">
                                <button className="btn btn-ghost" onClick={() => setBulkConfirm(false)} disabled={bulkBusy}>Cancel</button>
                                <button className="btn btn-danger" onClick={bulkDelete} disabled={bulkBusy} data-testid="confirm-bulk-delete">
                                    <Trash size={14} weight="bold" /> {bulkBusy ? "Deleting…" : "Delete all"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {recording && (
                <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="actual-modal">
                    <div className="rom-card w-full max-w-lg max-h-[90vh] overflow-y-auto">
                        <div className="rom-card-head">
                            <h3>Record actual invoice outcome</h3>
                            <button onClick={() => setRecording(null)}><X size={16} /></button>
                        </div>
                        <div className="rom-card-body space-y-4">
                            <div className="text-sm">
                                <div className="text-[var(--rom-text-2)]">Estimate <span className="font-mono">{recording.ref}</span> · {recording.loss_type}</div>
                                <div className="mt-1">ROM was <b>{formatCurrency(recording.result?.grand_total)}</b></div>
                            </div>

                            <div>
                                <label className="label">Invoice data quality</label>
                                <div className="inline-flex rounded-[4px] border border-[var(--rom-border-strong)] overflow-hidden w-full" data-testid="tier-toggle">
                                    <button
                                        onClick={() => setTier("A")}
                                        className={`flex-1 px-3 py-2 text-xs font-semibold transition-colors ${tier === "A" ? "bg-[var(--rom-navy)] text-white" : "bg-white hover:bg-slate-50"}`}
                                        data-testid="tier-a"
                                    >
                                        Tier A · Total only
                                    </button>
                                    <button
                                        onClick={() => setTier("B")}
                                        className={`flex-1 px-3 py-2 text-xs font-semibold transition-colors border-l border-[var(--rom-border-strong)] ${tier === "B" ? "bg-[var(--rom-orange)] text-white" : "bg-white hover:bg-slate-50"}`}
                                        data-testid="tier-b"
                                    >
                                        Tier B · Full A–E breakdown
                                    </button>
                                </div>
                                <div className="text-[11px] text-[var(--rom-text-2)] mt-1.5">
                                    {tier === "A"
                                        ? "Feeds total-level variance (engine vs actual, estimator vs actual)."
                                        : "Also feeds line-level calibration + the shadow ML model — pinpoints which schedule drifted."}
                                </div>
                            </div>

                            {tier === "A" ? (
                                <div>
                                    <label className="label">Final T&amp;M cost ($)</label>
                                    <input
                                        type="number" min="0" step="0.01"
                                        className="input font-mono"
                                        value={actualValue}
                                        onChange={(e) => setActualValue(e.target.value)}
                                        placeholder="e.g. 147500"
                                        autoFocus
                                        data-testid="actual-input"
                                    />
                                </div>
                            ) : (
                                <div className="space-y-2" data-testid="tier-b-breakdown">
                                    {BD_KEYS.map(({ k, label }) => (
                                        <div key={k}>
                                            <div className="flex items-center gap-3">
                                                <label className="text-sm flex-1">{label}</label>
                                                <input
                                                    type="number" min="0" step="0.01"
                                                    className="input font-mono w-40 text-right"
                                                    value={breakdown[k]}
                                                    onChange={(e) => setBreakdown((p) => ({ ...p, [k]: e.target.value }))}
                                                    placeholder="0.00"
                                                    data-testid={`bd-${k}`}
                                                />
                                            </div>
                                            {k === "supervision" && (
                                                <p className="text-[11px] text-[var(--rom-text-3)] mt-1 ml-0.5">
                                                    PM-tier labour only (Project Manager, Sr/Asst PM, Coordinator). Keep it OUT of <b>Field Labour</b> above so the calibration loop can tune supervision separately.
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                    <div className="flex items-center justify-between pt-2 border-t border-[var(--rom-border)] text-sm font-semibold">
                                        <span>Invoice total</span>
                                        <span className="font-mono" data-testid="bd-total">{formatCurrency(breakdownSum)}</span>
                                    </div>

                                    {estimateSubLines().length > 0 && (
                                        <div className="pt-3 mt-1 border-t border-[var(--rom-border)]" data-testid="sub-actuals-section">
                                            <div className="text-[11px] uppercase tracking-wider text-[var(--rom-text-3)] font-semibold mb-1">Per-vendor actuals (optional)</div>
                                            <div className="text-[11px] text-[var(--rom-text-2)] mb-2">Feeds the Schedule D vendor scorecard — which subs come in over/under quote.</div>
                                            <div className="space-y-1.5">
                                                {estimateSubLines().map((s) => (
                                                    <div key={s.idx} className="flex items-center gap-3" data-testid={`sub-actual-row-${s.idx}`}>
                                                        <div className="flex-1 text-sm truncate">
                                                            {s.item}{s.vendor ? ` · ${s.vendor}` : ""}
                                                            <span className="text-[11px] text-[var(--rom-text-3)] ml-1">quoted {formatCurrency(s.quoted)}</span>
                                                        </div>
                                                        <input
                                                            type="number" min="0" step="0.01"
                                                            className="input font-mono w-36 text-right py-1"
                                                            value={subActuals[s.idx] ?? ""}
                                                            onChange={(e) => setSubActuals((p) => ({ ...p, [s.idx]: e.target.value }))}
                                                            placeholder="actual $"
                                                            data-testid={`sub-actual-${s.idx}`}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {effectiveTotal > 0 && recording.result?.grand_total && (
                                <div className="p-3 rounded-[4px] bg-slate-50 text-sm" data-testid="variance-preview">
                                    Variance vs ROM: <b className="font-mono">{(((effectiveTotal - recording.result.grand_total) / recording.result.grand_total) * 100).toFixed(1)}%</b>
                                    {recording.engine_prediction?.grand_total && (
                                        <span className="ml-3 text-[var(--rom-text-2)]">
                                            vs raw engine: <b className="font-mono">{(((effectiveTotal - recording.engine_prediction.grand_total) / recording.engine_prediction.grand_total) * 100).toFixed(1)}%</b>
                                        </span>
                                    )}
                                </div>
                            )}
                            {err && <div className="text-xs text-[var(--rom-danger)]">{err}</div>}
                            <div className="flex justify-end gap-2">
                                <button className="btn btn-ghost" onClick={() => setRecording(null)}>Cancel</button>
                                <button className="btn btn-primary" onClick={saveActual} disabled={!effectiveTotal || effectiveTotal <= 0} data-testid="actual-save">Save &amp; close estimate</button>
                            </div>
                            <p className="text-[11px] text-[var(--rom-text-2)] leading-relaxed">
                                Recording an actual marks the estimate as <b>closed</b> and feeds the Coefficient Tuner. Tier B invoices additionally train the shadow ML layer once enough accumulate.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            <RomImportModal open={showRomImport} onClose={() => setShowRomImport(false)} clients={clients} onImported={load} />
            <InvoiceImportModal open={showInvoiceImport} onClose={() => setShowInvoiceImport(false)} onApplied={load} />
            <VarianceAnalysis jobId={varianceJobId} open={!!varianceJobId} onClose={() => setVarianceJobId(null)} />
            {logJob && <QuickLogTimeModal job={logJob} onClose={() => setLogJob(null)} onChanged={load} />}

            {notice && (
                <div
                    className={`fixed bottom-5 left-5 z-[60] max-w-sm px-4 py-3 rounded-[6px] shadow-lg text-sm font-medium flex items-center gap-3 ${notice.type === "err" ? "bg-[var(--rom-danger)] text-white" : "bg-[var(--rom-navy)] text-white"}`}
                    data-testid="rom-notice"
                >
                    <span>{notice.msg}</span>
                    <button onClick={() => setNotice(null)} className="text-white/70 hover:text-white text-xs" data-testid="rom-notice-dismiss">✕</button>
                </div>
            )}
        </div>
    );
}

function StatusChips({ statuses, approval, billingStatus }) {
    return (
        <div className="flex items-center gap-1 flex-wrap">
            {approval && (
                <span className="chip chip-green whitespace-nowrap" style={{ padding: "1px 6px", fontSize: "10px" }} data-testid="approval-chip" title={approval.by ? `Signed by ${approval.by}` : undefined}>
                    <CheckCircle size={9} weight="fill" /> {({ client: "Approved · Client", insurer: "Approved · Insurance", adjuster: "Approved · Adjuster" })[approval.recipient_type] || "Approved"}
                </span>
            )}
            <BillingStatusBadge status={billingStatus} />
            {statuses.map((s) => {
                const meta = {
                    draft: { cls: "chip-gray", icon: FileText, label: "Draft" },
                    complete: { cls: "chip-green", icon: CheckCircle, label: "Complete" },
                    shared: { cls: "chip-orange", icon: ShareNetwork, label: "Shared" },
                    sent: { cls: "chip-orange", icon: ShareNetwork, label: "Sent" },
                    approved: { cls: "chip-green", icon: CheckCircle, label: "Approved" },
                    in_progress: { cls: "chip-navy", icon: PencilSimple, label: "In progress" },
                    closed: { cls: "chip-green", icon: CheckCircle, label: "Closed" },
                }[s] || { cls: "chip-gray", icon: FileText, label: s };
                const Icon = meta.icon;
                return (
                    <span key={s} className={`chip ${meta.cls}`}>
                        <Icon size={10} weight="bold" /> {meta.label}
                    </span>
                );
            })}
        </div>
    );
}

function fmtDate(s) {
    if (!s) return "—";
    try {
        const d = new Date(s);
        const now = new Date();
        const diffMin = Math.floor((now - d) / 60000);
        if (diffMin < 1) return "just now";
        if (diffMin < 60) return `${diffMin}m ago`;
        if (diffMin < 24 * 60) return `${Math.floor(diffMin / 60)}h ago`;
        return d.toLocaleDateString();
    } catch { return ""; }
}
